import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  createOrgInviteApi,
  cancelOrgInviteApi,
  listOrgInvitesApiv2,
  resendOrgInviteApi,
  acceptOrgInviteApi,
  declineOrgInviteApi,
  type CreateOrgInviteRequestContent,
  type ListOrgInvitesResponseContent
} from '../../org-invites';

/**
 * [EQIQ-018:invites] Org-invite endpoints return generic 500s on validation
 * failures. Should return 400/404 with descriptive messages:
 * - Invalid invite ID
 * - Invite already accepted/declined
 * - User not authorized
 * - Invite expired
 */

/**
 * Grouped test component for all Invite endpoints
 */
const TestInvites: React.FC = () => {
  // Create Invite state
  const [createOrgId, setCreateOrgId] = useState('');
  const [createEmails, setCreateEmails] = useState('');
  const [createRole, setCreateRole] = useState('member');
  const [createResponse, setCreateResponse] = useState<any>(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // List Invites state
  const [listOrgId, setListOrgId] = useState('');
  const [listResponse, setListResponse] = useState<ListOrgInvitesResponseContent | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // Accept Invite state
  const [acceptOrgId, setAcceptOrgId] = useState('');
  const [acceptInviteId, setAcceptInviteId] = useState('');
  const [acceptResponse, setAcceptResponse] = useState<any>(null);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  // Decline Invite state
  const [declineOrgId, setDeclineOrgId] = useState('');
  const [declineInviteId, setDeclineInviteId] = useState('');
  const [declineResponse, setDeclineResponse] = useState<any>(null);
  const [declineLoading, setDeclineLoading] = useState(false);
  const [declineError, setDeclineError] = useState<string | null>(null);

  // Cancel Invite state
  const [cancelOrgId, setCancelOrgId] = useState('');
  const [cancelInviteId, setCancelInviteId] = useState('');
  const [cancelResponse, setCancelResponse] = useState<any>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // Resend Invite state
  const [resendOrgId, setResendOrgId] = useState('');
  const [resendInviteId, setResendInviteId] = useState('');
  const [resendResponse, setResendResponse] = useState<any>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  // Handlers
  const handleCreateInvite = async () => {
    if (!createOrgId.trim() || !createEmails.trim()) {
      setCreateError('Please enter both organization ID and email(s)');
      return;
    }

    setCreateLoading(true);
    setCreateError(null);

    try {
      const emailList = createEmails.split(',').map(e => e.trim()).filter(e => e.length > 0);
      const request: CreateOrgInviteRequestContent = {
        orgId: createOrgId.trim(),
        emails: emailList,
        role: createRole as any
      };
      const result = await createOrgInviteApi(request);
      setCreateResponse(result);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleListInvites = async () => {
    if (!listOrgId.trim()) {
      setListError('Please enter an organization ID');
      return;
    }

    setListLoading(true);
    setListError(null);

    try {
      const result = await listOrgInvitesApiv2({ orgId: listOrgId.trim() });
      setListResponse(result);
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setListLoading(false);
    }
  };

  const handleAcceptInvite = async () => {
    if (!acceptOrgId.trim() || !acceptInviteId.trim()) {
      setAcceptError('Please enter both organization ID and invite ID');
      return;
    }

    setAcceptLoading(true);
    setAcceptError(null);

    try {
      // [EQIQ-018:accept-invite] acceptOrgInvite returns 500 for validation
      // failures; should return 404 (not found), 400 (already accepted/declined),
      // or 400 (expired).
      const result = await acceptOrgInviteApi({
        orgId: acceptOrgId.trim(),
        inviteId: acceptInviteId.trim()
      });
      setAcceptResponse(result);
    } catch (err) {
      setAcceptError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setAcceptLoading(false);
    }
  };

  const handleDeclineInvite = async () => {
    if (!declineOrgId.trim() || !declineInviteId.trim()) {
      setDeclineError('Please enter both organization ID and invite ID');
      return;
    }

    setDeclineLoading(true);
    setDeclineError(null);

    try {
      const result = await declineOrgInviteApi({
        orgId: declineOrgId.trim(),
        inviteId: declineInviteId.trim()
      });
      setDeclineResponse(result);
    } catch (err) {
      setDeclineError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setDeclineLoading(false);
    }
  };

  const handleCancelInvite = async () => {
    if (!cancelOrgId.trim() || !cancelInviteId.trim()) {
      setCancelError('Please enter both organization ID and invite ID');
      return;
    }

    setCancelLoading(true);
    setCancelError(null);

    try {
      const result = await cancelOrgInviteApi({
        orgId: cancelOrgId.trim(),
        inviteId: cancelInviteId.trim()
      });
      setCancelResponse(result);
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleResendInvite = async () => {
    if (!resendOrgId.trim() || !resendInviteId.trim()) {
      setResendError('Please enter both organization ID and invite ID');
      return;
    }

    setResendLoading(true);
    setResendError(null);

    try {
      const result = await resendOrgInviteApi({
        orgId: resendOrgId.trim(),
        inviteId: resendInviteId.trim()
      });
      setResendResponse(result);
    } catch (err) {
      setResendError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#fff' }}>
        Invite Management APIs
      </Typography>

      {/* Create Invite */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Create Invite</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID"
              value={createOrgId}
              onChange={(e) => setCreateOrgId(e.target.value)}
              fullWidth
            />
            <TextField
              label="Email(s)"
              value={createEmails}
              onChange={(e) => setCreateEmails(e.target.value)}
              fullWidth
              placeholder="email@example.com, email2@example.com"
              helperText="Enter one or more emails separated by commas"
            />
            <TextField
              label="Role"
              value={createRole}
              onChange={(e) => setCreateRole(e.target.value)}
              fullWidth
              placeholder="admin, member, viewer, etc."
              helperText="Valid roles: primary_owner, admin, billing_admin, member, viewer, custom"
            />
            <Button
              variant="contained"
              onClick={handleCreateInvite}
              disabled={createLoading}
            >
              {createLoading ? <CircularProgress size={24} /> : 'Create Invite'}
            </Button>
            {createResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(createResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {createError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{createError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* List Invites */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">List Invites</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID"
              value={listOrgId}
              onChange={(e) => setListOrgId(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleListInvites}
              disabled={listLoading}
            >
              {listLoading ? <CircularProgress size={24} /> : 'List Invites'}
            </Button>
            {listResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
                  Invites ({Object.keys(listResponse.invites || {}).length}):
                </Typography>
                {listResponse.invites && Object.keys(listResponse.invites).length > 0 ? (
                  <List>
                    {Object.entries(listResponse.invites).map(([inviteId, invite], index) => (
                      <React.Fragment key={inviteId}>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {invite.invitedEmail}
                                <Chip label={invite.status} size="small" color={invite.status === 'pending' ? 'warning' : 'default'} />
                                <Chip label={invite.role} size="small" />
                              </Box>
                            }
                            secondary={
                              <>
                                Invite ID: {invite.orgInviteId}
                                <br />
                                Invited By: {invite.invitedByUserId}
                                {invite.customRoleName && (
                                  <>
                                    <br />
                                    Custom Role: {invite.customRoleName}
                                  </>
                                )}
                              </>
                            }
                          />
                        </ListItem>
                        {index < Object.keys(listResponse.invites).length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography>No invites found</Typography>
                )}
              </Paper>
            )}
            {listError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{listError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Accept Invite */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Accept Invite</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID"
              value={acceptOrgId}
              onChange={(e) => setAcceptOrgId(e.target.value)}
              fullWidth
            />
            <TextField
              label="Invite ID"
              value={acceptInviteId}
              onChange={(e) => setAcceptInviteId(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleAcceptInvite}
              disabled={acceptLoading}
              color="success"
            >
              {acceptLoading ? <CircularProgress size={24} /> : 'Accept Invite'}
            </Button>
            {acceptResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(acceptResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {acceptError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{acceptError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Decline Invite */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Decline Invite</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID"
              value={declineOrgId}
              onChange={(e) => setDeclineOrgId(e.target.value)}
              fullWidth
            />
            <TextField
              label="Invite ID"
              value={declineInviteId}
              onChange={(e) => setDeclineInviteId(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleDeclineInvite}
              disabled={declineLoading}
              color="warning"
            >
              {declineLoading ? <CircularProgress size={24} /> : 'Decline Invite'}
            </Button>
            {declineResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(declineResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {declineError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{declineError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Cancel Invite */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Cancel Invite</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID"
              value={cancelOrgId}
              onChange={(e) => setCancelOrgId(e.target.value)}
              fullWidth
            />
            <TextField
              label="Invite ID"
              value={cancelInviteId}
              onChange={(e) => setCancelInviteId(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleCancelInvite}
              disabled={cancelLoading}
              color="error"
            >
              {cancelLoading ? <CircularProgress size={24} /> : 'Cancel Invite'}
            </Button>
            {cancelResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(cancelResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {cancelError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{cancelError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Resend Invite */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Resend Invite</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID"
              value={resendOrgId}
              onChange={(e) => setResendOrgId(e.target.value)}
              fullWidth
            />
            <TextField
              label="Invite ID"
              value={resendInviteId}
              onChange={(e) => setResendInviteId(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleResendInvite}
              disabled={resendLoading}
            >
              {resendLoading ? <CircularProgress size={24} /> : 'Resend Invite'}
            </Button>
            {resendResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(resendResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {resendError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{resendError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default TestInvites;
